import { useEffect, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Carregando, Aviso } from "../../components/Layout";
import { api, fmt } from "../../lib/api";

type Registro = {
  sequencia: number; ator: string; perfil: string | null; acao: string;
  entidade: string; entidadeId: string | null;
  estadoAnterior: string | null; estadoNovo: string | null;
  ip: string | null; criadoEm: string;
};

type Pagina = { conteudo: Registro[]; totalElementos: number; totalPaginas: number };
type Verificacao = { integra: boolean; total: number; primeiraQuebra: number | null; detalhe: string };

/**
 * Log de auditoria encadeado.
 *
 * Em banco relacional, "imutável" é promessa que ninguém cumpre só com
 * permissão: quem tem acesso ao servidor altera uma linha. O que se garante é a
 * detecção — cada registro guarda o hash do anterior, e a verificação aponta
 * exatamente onde a cadeia foi quebrada.
 */
export function Auditoria() {
  const [dados, setDados] = useState<Pagina | null>(null);
  const [pagina, setPagina] = useState(0);
  const [verificacao, setVerificacao] = useState<Verificacao | null>(null);
  const [verificando, setVerificando] = useState(false);

  useEffect(() => {
    api.get<Pagina>(`/api/admin/auditoria?pagina=${pagina}&tamanho=50`)
      .then(setDados)
      .catch(() => setDados(null));
  }, [pagina]);

  async function verificar() {
    setVerificando(true);
    try { setVerificacao(await api.get<Verificacao>("/api/admin/auditoria/verificar")); }
    catch { setVerificacao(null); }
    finally { setVerificando(false); }
  }

  if (!dados) return <Carregando texto="Carregando o log" />;

  return (
    <div className="pilha pilha-6">
      <div className="linha linha-entre linha-quebra">
        <div>
          <h1>Auditoria</h1>
          <p className="texto-secundario">
            {dados.totalElementos} registro(s). Toda decisão, acesso a documento sensível e
            publicação fica registrada com autor, momento e estado anterior.
          </p>
        </div>
        <button className="btn btn-secundario" onClick={verificar} disabled={verificando}>
          {verificando ? <span className="carregando" /> : <Icon nome="escudo" tamanho={18} />}
          Verificar integridade da cadeia
        </button>
      </div>

      {verificacao && (
        <Aviso tipo={verificacao.integra ? "sucesso" : "erro"}
               titulo={verificacao.integra ? "Cadeia íntegra" : "Cadeia violada"}>
          {verificacao.detalhe}
          {verificacao.primeiraQuebra && (
            <> Primeiro registro inconsistente: sequência {verificacao.primeiraQuebra}.</>
          )}
        </Aviso>
      )}

      <div className="painel">
        <div className="tabela-rolagem">
          <table className="tabela tabela-cartoes">
            <caption className="so-leitor">Registros de auditoria, do mais recente ao mais antigo</caption>
            <thead>
              <tr>
                <th scope="col" className="col-num">#</th>
                <th scope="col">Quando</th>
                <th scope="col">Quem</th>
                <th scope="col">Ação</th>
                <th scope="col">Objeto</th>
                <th scope="col">Alteração</th>
              </tr>
            </thead>
            <tbody>
              {dados.conteudo.map((r) => (
                <tr key={r.sequencia}>
                  <td className="col-num texto-sutil" data-rotulo="Nº">{r.sequencia}</td>
                  <td className="num" data-rotulo="Quando">{fmt.dataHora(r.criadoEm)}</td>
                  <td data-rotulo="Quem">
                    {r.ator}
                    {r.perfil && <><br /><span className="texto-sutil">{r.perfil.toLowerCase()}</span></>}
                    {r.ip && <><br /><span className="texto-sutil mono">{r.ip}</span></>}
                  </td>
                  <td data-rotulo="Ação">
                    <span className={`etiqueta ${r.acao.includes("SENSIVEL") ? "etiqueta-pcd" : ""}`}>
                      {r.acao.replaceAll("_", " ").toLowerCase()}
                    </span>
                  </td>
                  <td className="texto-sutil" data-rotulo="Objeto">
                    {r.entidade}
                    {r.entidadeId ? ` #${r.entidadeId}` : ""}
                  </td>
                  <td style={{ maxWidth: "22rem" }} data-rotulo="Alteração">
                    {r.estadoAnterior && (
                      <div className="texto-sutil mono" style={{ fontSize: "var(--text-xs)" }}>
                        antes: {r.estadoAnterior}
                      </div>
                    )}
                    {r.estadoNovo && (
                      <div className="mono" style={{ fontSize: "var(--text-xs)" }}>
                        {r.estadoAnterior ? "depois: " : ""}{r.estadoNovo}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {dados.totalPaginas > 1 && (
          <div className="paginacao">
            <button className="btn btn-secundario btn-pequeno" disabled={pagina === 0}
                    onClick={() => setPagina((p) => p - 1)}>
              <Icon nome="esquerda" tamanho={14} /> Anterior
            </button>
            <span className="texto-sutil num">Página {pagina + 1} de {dados.totalPaginas}</span>
            <button className="btn btn-secundario btn-pequeno"
                    disabled={pagina >= dados.totalPaginas - 1}
                    onClick={() => setPagina((p) => p + 1)}>
              Próxima <Icon nome="direita" tamanho={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
